import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { apiClient } from './client'

// AssetCompletenessReport mirrors the backend payload from
// GET /api/v1/organizations/{orgCode}/assets/{assetUid}/completeness.
// This endpoint is not yet declared in the OpenAPI schema, so we type the
// response shape locally and bypass the typed client below.
export interface CompletenessMissing {
    property_code: string
    property_name: Record<string, string>
}

export interface CompletenessBucket {
    total: number
    filled: number
    missing: CompletenessMissing[]
}

export interface AssetCompletenessReport {
    asset_uid: string
    asset_type_code: string
    score: number
    mandatory: CompletenessBucket
    recommended: CompletenessBucket
}

// useAssetCompleteness fetches the completeness report for one asset.
// Reuses the apiClient (and therefore the auth middleware) by casting to any
// since the path is not declared in the generated OpenAPI types yet.
export function useAssetCompleteness(orgCode: string, assetUid: string | undefined) {
    return useQuery<AssetCompletenessReport>({
        queryKey: ['asset-completeness', orgCode, assetUid],
        enabled: !!assetUid,
        queryFn: async () => {
            const response = await (apiClient as unknown as {
                GET: (path: string, opts: { params: { path: { orgCode: string; assetUid: string } } }) => Promise<{ data?: AssetCompletenessReport }>
            }).GET(
                '/api/v1/organizations/{orgCode}/assets/{assetUid}/completeness',
                { params: { path: { orgCode, assetUid: assetUid! } } }
            )
            if (!response?.data) {
                throw new Error('Failed to fetch asset completeness')
            }
            return response.data as AssetCompletenessReport
        },
    })
}

interface Asset {
    uid: string
    name: string
    asset_type_uid: string
    parent_asset_uid?: string
    created_at?: string
    updated_at?: string
    children_count?: number
    flags?: string[]
    parent_chain?: boolean  // Used when asset_chain=true is specified
}

interface AssetType {
    uid: string
    code: string
    name: string | Record<string, string>
    emoji?: string
    created_at?: string
    updated_at?: string
}

// Hook to fetch asset types.
//
// The limit is not optional: the endpoint paginates at 20 by default and the
// ontology carries 100+ types, so without it callers silently receive a fifth
// of the list. Consumers resolve a type by uid (icon, code, per-type columns)
// and a missing one degrades to a generic folder with no code — see the
// assets tree.
export function useAssetTypes(orgCode: string) {
    return useQuery({
        queryKey: ['assetTypes', orgCode],
        queryFn: async (): Promise<AssetType[]> => {
            const response = await apiClient.GET('/api/v1/organizations/{orgCode}/asset-types', {
                params: { path: { orgCode }, query: { limit: 1000 } }
            })

            if (!response.data) {
                throw new Error('Failed to load asset types')
            }

            return response.data.data || []
        },
    })
}

// Hook to fetch assets with optional filtering by asset type(s), search query (q), parent_asset_uid, and sorting
export function useAssets(
    orgCode: string,
    assetTypeUids?: string | string[],
    q?: string,
    parentAssetUid?: string | null,
    sort?: string,
    include?: string[],
    flag?: string,
    assetChain?: boolean,
    archived?: boolean,
    // inTree: filters by asset_type.in_tree (spec 2026-07-24-02-asset-type-in-tree-flag).
    // Omitted → unfiltered, unchanged request shape/behavior.
    inTree?: boolean,
    // importUid: filters to the assets built by one import session
    // (spec 2026-07-27-10 §7 drill-down). Omitted → unchanged behavior.
    importUid?: string,
    // ancestorUid: returns every descendant (any depth) of that asset,
    // including members that got there through an aggregating relation
    // (spec 2026-08-16-03 §5 "see all equipment of the network").
    ancestorUid?: string
) {
    // Convert array of UIDs to comma-separated string for the API
    const assetTypeParam = Array.isArray(assetTypeUids)
        ? assetTypeUids.filter(uid => uid).join(',')
        : assetTypeUids

    const includeParam = include?.join(',')

    return useQuery({
        queryKey: ['assets', orgCode, assetTypeParam, q, parentAssetUid, sort, includeParam, flag, assetChain, archived, inTree, importUid, ancestorUid],
        queryFn: async (): Promise<Asset[]> => {
            const params: { path: { orgCode: string }, query?: Record<string, string> } = { path: { orgCode } }
            const query: Record<string, string> = {}

            if (assetTypeParam) {
                query.asset_type_uid = assetTypeParam
            }

            if (q) {
                query.q = q
            }

            if (parentAssetUid) {
                query.parent_asset_uid = parentAssetUid
            }

            if (sort) {
                query.sort = sort
            }

            if (includeParam) {
                query.include = includeParam
            }

            if (flag) {
                query.flag = flag
            }

            if (assetChain) {
                query.asset_chain = 'true'
            }

            if (archived) {
                query.archived = 'true'
            }

            if (inTree !== undefined) {
                query.in_tree = inTree ? 'true' : 'false'
            }

            if (importUid) {
                query.import_uid = importUid
            }

            if (ancestorUid) {
                query.ancestor_uid = ancestorUid
            }

            if (Object.keys(query).length > 0) {
                params.query = query
            }

            const response = await apiClient.GET('/api/v1/organizations/{orgCode}/assets', {
                params: {
                    path: { orgCode },
                    query
                }
            })

            if (!response.data) {
                throw new Error('Failed to load assets')
            }

            return response.data.data || []
        },
    })
}

// Hook to fetch asset count with specific flags (e.g., incomplete)
export function useAssetCount(
    orgCode: string,
    assetTypeUid?: string,
    flag?: string
) {
    return useQuery({
        queryKey: ['assetCount', orgCode, assetTypeUid, flag],
        queryFn: async (): Promise<number> => {
            const params: { path: { orgCode: string }, query?: Record<string, string> } = { path: { orgCode } }
            const query: Record<string, string> = {
                count: 'true'
            }

            if (assetTypeUid) {
                query.asset_type_uid = assetTypeUid
            }

            if (flag) {
                query.flag = flag
            }

            if (Object.keys(query).length > 0) {
                params.query = query
            }

            const response = await apiClient.GET('/api/v1/organizations/{orgCode}/assets', {
                params: {
                    path: { orgCode },
                    query
                }
            })

            if (!response.data) {
                throw new Error('Failed to load asset count')
            }

            // The API returns the count in the total field when count=true is specified
            return response.data.total || 0
        },
        staleTime: 30000, // Cache for 30 seconds since counts might be more volatile
    })
}

// Interface for updating assets
interface UpdateAssetRequest {
    name?: string
    asset_type_uid?: string
    parent_asset_uid?: string
    start_date?: string
    end_date?: string
    external_ids?: Record<string, unknown>
    properties?: Record<string, unknown>
}

// Hook to update an asset (including moving it to a new parent)
export function useUpdateAsset(orgCode: string, assetUid: string, options?: { onSuccess?: (asset: Asset) => void, onError?: (error: Error) => void }) {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: async (assetData: UpdateAssetRequest): Promise<Asset> => {
            const response = await apiClient.PATCH('/api/v1/organizations/{orgCode}/assets/{assetUid}', {
                params: { path: { orgCode, assetUid } },
                body: assetData as any
            })

            if (!response.data) {
                throw new Error('Failed to update asset')
            }

            return response.data
        },
        onSuccess: (asset) => {
            // Invalidate and refetch assets to update the tree structure
            queryClient.invalidateQueries({ queryKey: ['assets', orgCode] })
            queryClient.setQueryData(['asset', orgCode, asset.uid], asset)

            options?.onSuccess?.(asset)
        },
        onError: (error: Error) => {
            options?.onError?.(error)
        }
    })
}

// User interfaces
interface User {
    uid: string
    name?: string
    email?: string  // Optional since only required for type 'user'
    code?: string | null
    type: 'contact' | 'user' | 'user_group' | 'company' | 'system' | 'party_group' | 'establishment'
    organization_uid?: string  // Optional, not always included in API responses
    parent_user_uid?: string | null
    parent_user_name?: string | null
    nb_children?: number | null
    properties?: Record<string, unknown>
    // Not yet surfaced by the API for users (Flags is json:"-" server-side —
    // see internal/models/users.go). Read defensively so the establishment
    // "to_approve" badge (spec: contact-core-info-frontend) starts working the
    // moment a future slice exposes it, without requiring a frontend change.
    flags?: string[]
    picture?: {
        document_uid?: string
    } | null
    created_at?: string
    updated_at?: string
}

interface UserFilters {
    type?: 'contact' | 'user' | 'user_group' | 'company'
    q?: string
    page?: number
    limit?: number
    parent_uid?: string  // UUID to get children of a user, or "null" to get root-only contacts
    include?: string[]   // e.g. ["nb_children", "picture"]
}

interface CreateUserRequest {
    name: string
    email?: string  // Optional since only required for type 'user'
    type: 'contact' | 'user' | 'user_group' | 'company' | 'establishment'
    parent_user_uid?: string
    properties?: Record<string, unknown>
}

interface UpdateUserRequest {
    name?: string
    email?: string
    type?: 'contact' | 'user' | 'user_group' | 'company' | 'establishment'
    code?: string | null
    parent_user_uid?: string | null
    properties?: Record<string, unknown>
}

// Error thrown by useUpdateUser (and useCreateUser) when the backend returns a
// structured error. Keeps the HTTP status and the machine-readable `code`
// accessible so callers can translate to user-facing copy.
export class UserMutationError extends Error {
    status: number
    errorCode?: string
    constructor(message: string, status: number, errorCode?: string) {
        super(message)
        this.name = 'UserMutationError'
        this.status = status
        this.errorCode = errorCode
    }
}

// Hook to fetch users with optional filtering
// Pass filters=undefined to disable the query entirely
export function useUsers(orgCode: string, filters?: UserFilters | null) {
    return useQuery({
        queryKey: ['users', orgCode, filters],
        enabled: filters !== null,
        queryFn: async (): Promise<User[]> => {
            const params: { path: { orgCode: string }, query?: Record<string, string> } = { path: { orgCode } }
            const query: Record<string, string> = { include: 'picture' }

            if (filters?.type) {
                query.type = filters.type
            }

            if (filters?.q) {
                query.q = filters.q
            }

            if (filters?.page) {
                query.page = String(filters.page)
            }

            if (filters?.limit) {
                query.limit = String(filters.limit)
            }

            if (filters?.parent_uid) {
                query.parent_uid = filters.parent_uid
            }

            if (filters?.include?.length) {
                query.include = [query.include, ...filters.include].filter(Boolean).join(',')
            }

            if (Object.keys(query).length > 0) {
                params.query = query
            }

            const response = await apiClient.GET('/api/v1/organizations/{orgCode}/users', {
                params: {
                    path: { orgCode },
                    query
                }
            })

            if (!response.data) {
                throw new Error('Failed to load users')
            }

            return response.data.data || []
        },
    })
}

// Hook to fetch a single user by UID
export function useUser(orgCode: string, userUid: string) {
    return useQuery({
        queryKey: ['user', orgCode, userUid],
        queryFn: async (): Promise<User> => {
            const response = await apiClient.GET('/api/v1/organizations/{orgCode}/users/{userUid}', {
                params: {
                    path: { orgCode, userUid },
                    query: { include: ['picture'] }
                }
            })

            if (!response.data) {
                throw new Error('Failed to load user')
            }

            return response.data
        },
    })
}

// Hook to create a new user
export function useCreateUser(orgCode: string, options?: { onSuccess?: (user: User) => void, onError?: (error: Error) => void }) {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: async (userData: CreateUserRequest): Promise<User> => {
            const response = await apiClient.POST('/api/v1/organizations/{orgCode}/users', {
                params: { path: { orgCode } },
                body: {
                    name: userData.name,
                    email: userData.email || '',
                    type: userData.type,
                    parent_user_uid: userData.parent_user_uid,
                    properties: userData.properties as Record<string, never> || {}
                }
            })

            if (response.error || !response.data) {
                const status = (response.response?.status) ?? 0
                const err = response.error as { code?: string; title?: string; message?: string } | undefined
                throw new UserMutationError(err?.title || err?.message || 'Failed to create user', status, err?.code)
            }

            return response.data
        },
        onSuccess: (user) => {
            // Invalidate and refetch users list
            queryClient.invalidateQueries({ queryKey: ['users', orgCode] })
            queryClient.setQueryData(['user', orgCode, user.uid], user)

            options?.onSuccess?.(user)
        },
        onError: (error: Error) => {
            options?.onError?.(error)
        }
    })
}

// Hook to update an existing user
export function useUpdateUser(orgCode: string, userUid: string, options?: { onSuccess?: (user: User) => void, onError?: (error: Error) => void }) {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: async (userData: UpdateUserRequest): Promise<User> => {
            // Only forward the fields that are explicitly provided. In
            // particular, `code` must be forwardable as an explicit `null` so
            // the backend can clear it.
            const body: Record<string, unknown> = {}
            if (userData.name !== undefined) body.name = userData.name
            if (userData.email !== undefined) body.email = userData.email
            if (userData.type !== undefined) body.type = userData.type
            if (userData.parent_user_uid !== undefined) body.parent_user_uid = userData.parent_user_uid
            if (userData.properties !== undefined) body.properties = userData.properties
            if (Object.prototype.hasOwnProperty.call(userData, 'code')) body.code = userData.code

            const response = await apiClient.PATCH('/api/v1/organizations/{orgCode}/users/{userUid}', {
                params: { path: { orgCode, userUid } },
                body: body as any
            })

            if (response.error || !response.data) {
                const status = (response.response?.status) ?? 0
                const err = response.error as { code?: string; title?: string; message?: string } | undefined
                throw new UserMutationError(err?.title || err?.message || 'Failed to update user', status, err?.code)
            }

            return response.data as User
        },
        onSuccess: (user) => {
            // Invalidate and refetch users list and individual user
            queryClient.invalidateQueries({ queryKey: ['users', orgCode] })
            queryClient.setQueryData(['user', orgCode, user.uid], user)

            options?.onSuccess?.(user)
        },
        onError: (error: Error) => {
            options?.onError?.(error)
        }
    })
}

// Hook to delete a user
export function useDeleteUser(orgCode: string, options?: { onSuccess?: () => void, onError?: (error: Error) => void }) {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: async (userUid: string): Promise<void> => {
            const response = await apiClient.DELETE('/api/v1/organizations/{orgCode}/users/{userUid}', {
                params: { path: { orgCode, userUid } }
            })

            if (response.error) {
                throw new Error('Failed to delete user')
            }
        },
        onSuccess: (_, userUid) => {
            // Invalidate and refetch users list
            queryClient.invalidateQueries({ queryKey: ['users', orgCode] })
            queryClient.removeQueries({ queryKey: ['user', orgCode, userUid] })

            options?.onSuccess?.()
        },
        onError: (error: Error) => {
            options?.onError?.(error)
        }
    })
}

// Hook to merge a user into another user. The source user is folded into the
// target: its links move to the target and it becomes an archived child.
export function useMergeUser(orgCode: string, options?: { onSuccess?: (target: User) => void, onError?: (error: Error) => void }) {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: async ({ sourceUid, targetUid }: { sourceUid: string, targetUid: string }): Promise<User> => {
            const response = await apiClient.POST('/api/v1/organizations/{orgCode}/users/{userUid}/merge', {
                params: { path: { orgCode, userUid: sourceUid } },
                body: { target_user_uid: targetUid },
            })

            if (response.error || !response.data) {
                const status = (response.response?.status) ?? 0
                const err = response.error as { code?: string; title?: string; message?: string } | undefined
                throw new UserMutationError(err?.title || err?.message || 'Failed to merge user', status, err?.code)
            }

            return response.data as User
        },
        onSuccess: (target, { sourceUid }) => {
            // The source is now archived; drop its detail cache and refresh lists
            // (and any ancestor chains built from other users' rows).
            queryClient.removeQueries({ queryKey: ['user', orgCode, sourceUid] })
            queryClient.invalidateQueries({ queryKey: ['users', orgCode] })
            queryClient.invalidateQueries({ queryKey: ['userAncestors', orgCode] })
            queryClient.setQueryData(['user', orgCode, target.uid], target)

            options?.onSuccess?.(target)
        },
        onError: (error: Error) => {
            options?.onError?.(error)
        }
    })
}

// Hook to delete multiple users (bulk delete)
export function useDeleteUsers(orgCode: string, options?: { onSuccess?: () => void, onError?: (error: Error) => void }) {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: async (userUids: string[]): Promise<void> => {
            // For bulk delete, we'll delete each user individually
            // In a real implementation, you might have a bulk delete endpoint
            const promises = userUids.map(userUid =>
                apiClient.DELETE('/api/v1/organizations/{orgCode}/users/{userUid}', {
                    params: { path: { orgCode, userUid } }
                })
            )

            const responses = await Promise.all(promises)

            // Check if any requests failed
            const failed = responses.filter(response => response.error)
            if (failed.length > 0) {
                throw new Error(`Failed to delete ${failed.length} user(s)`)
            }
        },
        onSuccess: (_, userUids) => {
            // Invalidate and refetch users list
            queryClient.invalidateQueries({ queryKey: ['users', orgCode] })

            // Remove individual user queries
            userUids.forEach(userUid => {
                queryClient.removeQueries({ queryKey: ['user', orgCode, userUid] })
            })

            options?.onSuccess?.()
        },
        onError: (error: Error) => {
            options?.onError?.(error)
        }
    })
}

// Hook to get user picture
export function useUserPicture(orgCode: string, userUid: string) {
    return useQuery({
        queryKey: ['userPicture', orgCode, userUid],
        queryFn: async () => {
            const response = await apiClient.GET('/api/v1/organizations/{orgCode}/users/{userUid}/picture', {
                params: { path: { orgCode, userUid } }
            })

            if (!response.data) {
                throw new Error('Failed to load user picture')
            }

            return response.data
        },
    })
}

// Hook to update user picture
export function useUpdateUserPicture(orgCode: string, userUid: string, options?: { onSuccess?: (data: unknown) => void, onError?: (error: Error) => void }) {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: async (formData: FormData) => {
            const response = await apiClient.PUT('/api/v1/organizations/{orgCode}/users/{userUid}/picture', {
                params: { path: { orgCode, userUid } },
                body: formData as any
            })

            if (!response.data) {
                throw new Error('Failed to update user picture')
            }

            return response.data
        },
        onSuccess: (data) => {
            // Invalidate relevant queries
            queryClient.invalidateQueries({ queryKey: ['userPicture', orgCode, userUid] })
            queryClient.invalidateQueries({ queryKey: ['user', orgCode, userUid] })

            options?.onSuccess?.(data)
        },
        onError: (error: Error) => {
            options?.onError?.(error)
        }
    })
}

// Organizations

interface Organization {
    uid: string
    code: string
    name: string
    is_template?: boolean
    created_at?: string
    updated_at?: string
    picture?: {
        document_uid?: string
    } | null
}

interface OrganizationFilters {
    is_template?: boolean
    q?: string
    page?: number
    limit?: number
}

// Hook to fetch organizations
export function useOrganizations(filters?: OrganizationFilters) {
    return useQuery({
        queryKey: ['organizations', filters],
        queryFn: async (): Promise<Organization[]> => {
            const params: { query?: Record<string, string> } = {}
            const query: Record<string, string> = {}

            if (filters?.is_template !== undefined) {
                query.is_template = filters.is_template.toString()
            }

            if (filters?.q) {
                query.q = filters.q
            }

            if (filters?.page) {
                query.page = filters.page.toString()
            }

            if (filters?.limit) {
                query.limit = filters.limit.toString()
            }

            if (Object.keys(query).length > 0) {
                params.query = query
            }

            const response = await apiClient.GET('/api/v1/organizations', {
                params: params as any
            })

            if (!response.data) {
                throw new Error('Failed to load organizations')
            }

            return response.data.data || []
        },
    })
}

// Hook to fetch single organization
export function useOrganization(orgCode: string, include?: string[]) {
    return useQuery({
        queryKey: ['organization', orgCode, include],
        retry: false,
        queryFn: async (): Promise<Organization> => {
            const params: { path: { orgCode: string }, query?: Record<string, string> } = { path: { orgCode } }

            if (include && include.length > 0) {
                params.query = { include: include.join(',') }
            }

            const response = await apiClient.GET('/api/v1/organizations/{orgCode}', {
                params: params as any
            })

            if (response.error) {
                throw new Error(JSON.stringify(response.error))
            }

            if (!response.data) {
                throw new Error('Failed to load organization')
            }

            return response.data
        },
    })
}

// Hook to create organization
export function useCreateOrganization(options?: { onSuccess?: (org: Organization) => void, onError?: (error: Error) => void }) {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: async (data: { code: string, name: string, template_organization_id?: string, is_template?: boolean, jurisdiction: string }) => {
            const response = await apiClient.POST('/api/v1/organizations', {
                body: data as any
            })

            if (!response.data) {
                throw new Error('Failed to create organization')
            }

            return response.data
        },
        onSuccess: (data) => {
            // Invalidate organizations list
            queryClient.invalidateQueries({ queryKey: ['organizations'] })

            options?.onSuccess?.(data)
        },
        onError: (error: Error) => {
            options?.onError?.(error)
        }
    })
}

// Hook to update organization
export function useUpdateOrganization(orgCode: string, options?: { onSuccess?: (org: Organization) => void, onError?: (error: Error) => void }) {
    const queryClient = useQueryClient()

    return useMutation({
        // `locale` is not a column — the backend writes it into the `locale`
        // organization setting — and `code` is the slug, which only a platform
        // admin may send. One form, one call (spec 2026-09-09-10 §3).
        mutationFn: async (data: { name?: string, is_template?: boolean, code?: string, locale?: 'en' | 'fr', jurisdiction?: string, currency?: string }) => {
            const response = await apiClient.PATCH('/api/v1/organizations/{orgCode}', {
                params: { path: { orgCode } },
                body: data as any
            })

            if (!response.data) {
                // The typed error body carries the code the page branches on
                // (invalid_locale, organization_code_taken, …). Serialising it
                // keeps parseErrorCode's contract, which reads `err.message`.
                throw new Error(JSON.stringify(response.error ?? { code: 'bad_request' }))
            }

            return response.data
        },
        onSuccess: (data) => {
            // Invalidate organizations list and specific organization
            queryClient.invalidateQueries({ queryKey: ['organizations'] })
            queryClient.invalidateQueries({ queryKey: ['organization', orgCode] })
            // `useOrganization` is an openapi-react-query call, so its key is
            // ['get', '<path>', {params}] — invalidating ['organization', code]
            // alone would leave the page showing the value it just replaced.
            queryClient.invalidateQueries({
                predicate: (query) => {
                    const key = query.queryKey as any[]
                    return key.length >= 2 && key[0] === 'get' &&
                        key[1] === '/api/v1/organizations/{orgCode}'
                },
            })
            // A renamed org must update the sidebar brand (AppSidebar reads
            // this same membership summary) without a reload.
            queryClient.invalidateQueries({ queryKey: ['my-organizations'] })

            options?.onSuccess?.(data)
        },
        onError: (error: Error) => {
            options?.onError?.(error)
        }
    })
}

// Hook to update organization picture
export function useUpdateOrganizationPicture(orgCode: string, options?: { onSuccess?: (data: unknown) => void, onError?: (error: Error) => void }) {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: async (formData: FormData) => {
            const response = await apiClient.PUT('/api/v1/organizations/{orgCode}/picture', {
                params: { path: { orgCode } },
                body: formData as any
            })

            if (!response.data) {
                throw new Error('Failed to update organization picture')
            }

            return response.data
        },
        onSuccess: (data) => {
            // Invalidate relevant queries
            queryClient.invalidateQueries({ queryKey: ['organization', orgCode] })
            queryClient.invalidateQueries({ queryKey: ['organizations'] })
            queryClient.invalidateQueries({ queryKey: ['my-organizations'] })

            options?.onSuccess?.(data)
        },
        onError: (error: Error) => {
            options?.onError?.(error)
        }
    })
}

// Hook to delete organization picture
export function useDeleteOrganizationPicture(orgCode: string, options?: { onSuccess?: () => void, onError?: (error: Error) => void }) {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: async () => {
            const response = await apiClient.DELETE('/api/v1/organizations/{orgCode}/picture', {
                params: { path: { orgCode } }
            })

            return response
        },
        onSuccess: () => {
            // Invalidate relevant queries
            queryClient.invalidateQueries({ queryKey: ['organization', orgCode] })
            queryClient.invalidateQueries({ queryKey: ['organizations'] })
            queryClient.invalidateQueries({ queryKey: ['my-organizations'] })

            options?.onSuccess?.()
        },
        onError: (error: Error) => {
            options?.onError?.(error)
        }
    })
}